import { useEffect, useState } from "react";
import { api, fmtDate, today } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ArrowDownUp } from "lucide-react";
import { toast } from "sonner";

export default function StockAdjustments() {
    const [items, setItems] = useState([]);
    const [products, setProducts] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ date: today(), item_id: "", quantity: 0, reason: "Damage", notes: "" });

    const load = async () => {
        const [a, p] = await Promise.all([api.get("/stock-adjustments"), api.get("/items")]);
        setItems(a.data); setProducts(p.data);
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        await api.post("/stock-adjustments", { ...form, quantity: Number(form.quantity) });
        toast.success("Adjustment posted");
        setOpen(false);
        await load();
    };

    return (
        <>
            <PageHeader kicker="INVENTORY" title="Stock Adjustments" subtitle="Add or remove stock for damages, audits, and corrections."
                actions={<Button onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1.5" />New adjustment</Button>} />
            <PageBody>
                <div className="bg-card border border-border rounded-md overflow-hidden">
                    {items.length === 0 ? <EmptyState icon={ArrowDownUp} title="No adjustments" /> :
                        <table className="fin-table">
                            <thead><tr><th>Number</th><th>Date</th><th>Item</th><th className="num">Quantity</th><th>Reason</th></tr></thead>
                            <tbody>
                                {items.map((a) => {
                                    const it = products.find((p) => p.id === a.item_id);
                                    return (
                                        <tr key={a.id}>
                                            <td className="font-mono text-[12.5px]">{a.number}</td>
                                            <td className="text-[12px] text-muted-foreground">{fmtDate(a.date)}</td>
                                            <td>{it?.name}</td>
                                            <td className={`num ${a.quantity < 0 ? "text-destructive" : "text-positive"}`}>{a.quantity > 0 ? "+" : ""}{a.quantity}</td>
                                            <td>{a.reason}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>}
                </div>
            </PageBody>
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="right" className="w-full sm:max-w-[440px] p-0 flex flex-col">
                    <SheetHeader className="border-b border-border px-6 py-4"><SheetTitle className="text-xl font-semibold tracking-tight">New adjustment</SheetTitle></SheetHeader>
                    <form className="flex-1 overflow-y-auto p-6 space-y-4" onSubmit={submit}>
                        <div><Label>Item</Label>
                            <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Pick item" /></SelectTrigger>
                                <SelectContent>{products.filter((p) => p.track_inventory).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
                        <div><Label>Quantity (signed)</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="-5 to remove, +10 to add" className="mt-1.5" /></div>
                        <div><Label>Reason</Label>
                            <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>{["Damage", "Theft", "Audit correction", "Sample / write-off", "Recount"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
                        <Button type="submit" className="w-full">Post</Button>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
}
